import { Box, Flex, Heading, Text } from "@radix-ui/themes";
import { Link, useNavigate } from "@tanstack/react-router";
import { LayoutDashboard, LogOut, Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuthStore } from "@/stores/auth.store";
import { UserAvatar } from "@/components/ui/user-avatar";
import { buildUserFileAvatarUrl } from "@/lib/avatars";

interface NavContentProps {
  onNavigate?: () => void;
}

export function NavContent({ onNavigate }: NavContentProps) {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const { t } = useTranslation();

  function handleLogout() {
    logout(); // pb.authStore.clear() + Zustand sync via onChange
    navigate({ to: "/login" });
    onNavigate?.();
  }

  function handleNavClick() {
    onNavigate?.();
  }

  return (
    <Box
      asChild
      style={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        minHeight: "100%",
        backgroundColor: "var(--gray-1)",
      }}
    >
      <nav style={{ minHeight: "100dvh", display: "flex", flexDirection: "column" }}>
        <Box px="4" py="5">
          <Heading size="5" weight="bold">
            mem.pm
          </Heading>
        </Box>

        <Flex direction="column" gap="1" px="2" flexGrow="1">
          <Link to="/dashboard" style={{ textDecoration: "none" }} onClick={handleNavClick}>
            {({ isActive }) => (
              <Flex
                align="center"
                gap="2"
                px="3"
                py="2"
                style={{
                  borderRadius: "var(--radius-3)",
                  backgroundColor: isActive ? "var(--accent-3)" : "transparent",
                  color: isActive ? "var(--accent-11)" : "var(--gray-11)",
                  cursor: "pointer",
                  minHeight: 44,
                }}
              >
                <LayoutDashboard size={16} />
                <Text size="2" weight={isActive ? "medium" : "regular"}>
                  {t("nav.dashboard")}
                </Text>
              </Flex>
            )}
          </Link>

          <Link
            to="/dashboard"
            search={{ new: true }}
            style={{ textDecoration: "none" }}
            onClick={handleNavClick}
          >
            <Flex
              align="center"
              gap="2"
              px="3"
              py="2"
              style={{
                borderRadius: "var(--radius-3)",
                color: "var(--gray-10)",
                cursor: "pointer",
                minHeight: 44,
              }}
            >
              <Plus size={16} />
              <Text size="2">{t("nav.new_board")}</Text>
            </Flex>
          </Link>
        </Flex>

        <Box px="2" py="3" style={{ borderTop: "1px solid var(--gray-4)", marginTop: "auto" }}>
          <Flex align="center" gap="2" px="3" py="2">
            <Link
              to="/profile"
              style={{ textDecoration: "none", flexGrow: 1, minWidth: 0 }}
              onClick={handleNavClick}
            >
              {({ isActive }) => (
                <Flex
                  align="center"
                  gap="2"
                  style={{
                    borderRadius: "var(--radius-3)",
                    backgroundColor: isActive ? "var(--accent-3)" : "transparent",
                    padding: "4px 6px",
                    minHeight: 36,
                  }}
                >
                  <UserAvatar
                    size="1"
                    radius="full"
                    src={user ? buildUserFileAvatarUrl(user) : null}
                    name={user?.name}
                    version={user?.avatar_version}
                    style={{ flexShrink: 0, width: 28, height: 28 }}
                  />
                  <Flex direction="column" style={{ minWidth: 0 }}>
                    <Text size="2" weight="medium" truncate>
                      {user?.name}
                    </Text>
                    <Text size="1" color="gray" truncate>
                      {user?.email}
                    </Text>
                  </Flex>
                </Flex>
              )}
            </Link>
            <Box asChild style={{ cursor: "pointer", color: "var(--gray-9)", flexShrink: 0 }}>
              <button type="button" aria-label={t("nav.logout")} onClick={handleLogout} style={{ minWidth: 32, minHeight: 32, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <LogOut size={16} />
              </button>
            </Box>
          </Flex>
        </Box>
      </nav>
    </Box>
  );
}
